"use client";
import { Box, Container, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import ArmyImage from "../../_shared/ArmyImage";

import { GameSystems, GenericallyNamed } from "@/data/types";
import useGetGenericName from "@/hooks/useGetGenericName";
import { getCdnUrl } from "@/services";
import type { JSX } from "react";

type Props = {
  armyData:
    | (GenericallyNamed & {
        bannerImagePath?: string;
        official?: boolean;
        isCreator?: boolean;
        gameSystemId?: number;
      })
    | null;
  name?: string;
  actions: JSX.Element;
  hideVignette?: boolean;
};

export default function ArmyInfoHero({ armyData, name, actions, hideVignette }: Props) {
  const theme = useTheme();
  const mdScreen = useMediaQuery(theme.breakpoints.up("md"));
  const armyName = useGetGenericName()(armyData);
  const hasBanner = !!armyData.bannerImagePath;

  // we missuse this to detect those that have their own banner
  const showBanner = true; // (armyData?.official || armyData?.isCreator) ?? false;

  if (!mdScreen) return null;

  const videoMode = armyData.bannerImagePath?.endsWith("webm");
  const isLocal = typeof window !== "undefined" && window.location.href.includes("localhost");
  const imageStyle = {
    background: "#000000",
    backgroundImage: `url(${
      (showBanner && hasBanner ? getCdnUrl(armyData.bannerImagePath) : null) ||
      getCdnUrl(`app/${GameSystems[armyData.gameSystemId || 2]}_cover.jpg`)
    })`,
    backgroundSize: "cover",
    backgroundPosition: "center"
  };

  return (
    <Box
      sx={{
        ...(videoMode ? { display: "flex", justifyContent: "center" } : imageStyle),
        maxHeight: "80vh",
        width: "100%",
        height: {
          xs: showBanner ? "0" : "100px",
          sm: showBanner ? "0" : "100px"
        },
        position: "relative"
      }}
    >
      {videoMode && !isLocal && (
        <video loop autoPlay muted playsInline>
          <source src={armyData.bannerImagePath} type="video/webm" />
        </video>
      )}
      <Box
        sx={{
          backdropFilter: hasBanner ? null : "blur(10px)",
          boxShadow: hideVignette
            ? null
            : mdScreen
              ? "0 -50px 200px 50px black inset"
              : "0 -20px 100px  black inset",
          height: "100%",
          position: "relative",
          display: "flex"
        }}
      >
        <Container
          sx={{
            display: { xs: "none", md: "block" },
            py: { xs: 2, sm: 4 },
            pb: 2,
            alignSelf: "flex-end"
          }}
        >
          <Stack
            direction="column"
            sx={{
              justifyContent: "center",
              position: "relative"
            }}
          >
            <Stack>
              <Typography
                variant="h1"
                sx={{
                  pt: 1,
                  mb: 4,
                  color: "#FFFFFF",
                  fontWeight: 400,
                  fontFamily: GameSystems[armyData.gameSystemId]?.startsWith("gf")
                    ? "GrimdarkTitle"
                    : "AgeOfFantasyTitle",
                  textTransform: "uppercase",
                  fontSize: "3rem",
                  textShadow: "2px 2px 4px rgba(0,0,0,0.75)"
                }}
                align="center"
              >
                {armyName || name}
              </Typography>
              <Stack sx={{ maxWidth: "360px", mx: "auto" }}>{actions}</Stack>
            </Stack>
          </Stack>
        </Container>
        {showBanner ? (
          <Box
            sx={{
              display: { xs: "block", md: "none" },
              position: "absolute",
              bottom: "-20px",
              width: {
                xs: "100px",
                sm: "120px"
              },
              height: {
                xs: "100px",
                sm: "120px"
              },
              left: 0,
              px: 2
            }}
          >
            {armyData.official && <ArmyImage size="100%" armyBook={armyData} shadow={true} />}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
